/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from "react";
import PrivateComponent from "../../auth/PrivateComponent";
import { FullNote } from "../../interfaces/mortgageNote";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faRightFromBracket,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import useAuth from "../../hooks/useAuth";
import { useNavigate, useLocation } from "react-router-dom";
import {
  faHeadset,
  faPaperPlane,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import useModal from "../../hooks/useModal";
import { adminApi } from "../../api";
import useAlert from "../../hooks/useAlert";
import { debounce } from "lodash";
import LoadingSpinner from "../loading/LoadingSpinner";

interface AdminToolbarProps {
  note?: FullNote;
  onSearch?: (query: string) => void;
}

const AdminToolbar = ({ note, onSearch }: AdminToolbarProps) => {
  const { logout } = useAuth();

  const { openModalWith, setShowModal } = useModal();

  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const [isSending, setIsSending] = useState<boolean>(false);

  const { setAlert } = useAlert();

  const navigate = useNavigate();

  const location = useLocation();

  const cardRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (onSearch) {
        onSearch(query);
      }
    }, 300),
    [onSearch]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get("search") || "";
    setSearchQuery(query);
  }, [location.search]);

  const handleLogout = () => {
    return logout();
  };

  const handleDeleteSubmit = async () => {
    if (note?.id) {
      try {
        setIsDeleting(true);
        await adminApi.deleteNote(note?.id);
        setShowModal(false);
        setAlert({
          message: `Note with ID-${note?.id} has been deleted.`,
          type: "info",
          display: true,
        });
        navigate("/");
      } catch (error) {
        console.error(error);
        setShowModal(false);
        setAlert({
          message: `Error deleting Note with ID-${note?.id}.`,
          type: "error",
          display: true,
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleNewNote = () => {
    navigate("/admin/note/new");
  };

  const handleEditNote = () => {
    navigate(`/admin/note/edit/${note?.id}`);
  };

  const handleSendNote = async () => {
    if (note?.id) {
      try {
        setIsSending(true);
        await adminApi.sendNote(note?.id);
        setAlert({
          message: `Note with ID-${note?.id} has been emailed.`,
          type: "info",
          display: true,
        });
      } catch (error) {
        console.error(error);
        setAlert({
          message: `Error emailing Note with ID-${note?.id}.`,
          type: "error",
          display: true,
        });
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleDeleteNote = () => {
    openModalWith({
      title: "",
      body: (
        <div className="text-red-600 font-medium flex flex-row gap-2 text-center items-center justify-center -mt-4 mb-8">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          Are you sure you want to delete this note? This action cannot be
          undone!
        </div>
      ),
      onSubmit: handleDeleteSubmit,
      submitLabel: "Delete",
      submitIcon: faTrash,
      submitLoading: isDeleting,
      submitColor: "bg-red-600 hover:bg-red-500",
    });
  };

  return (
    <PrivateComponent>
      <div
        className="navbar bg-gray-200 rounded-lg w-full flex flex-row "
        ref={cardRef}
      >
        <div className="relative">
          <h1 className="text-xl font-semibold text-base-100 pl-4">Admin</h1>
        </div>

        <div className="navbar-end gap-1 sm:gap-2 w-full">
          {!note && (
            <div className="form-control w-full  sm:w-auto">
              <input
                type="text"
                placeholder="Search"
                className="input input-bordered bg-white w-full sm:w-96 text-black"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          )}

          {note ? (
            <div className="flex flex-row gap-1 sm:gap-2">
              <button
                className="btn btn-md font-semibold hover:bg-red-500 hover:text-white hover:border-red-500"
                onClick={handleDeleteNote}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span className="hidden sm:block">Delete</span>
              </button>
              <button
                className="btn btn-md font-semibold hover:bg-success hover:text-white hover:border-success "
                onClick={handleSendNote}
              >
                {isSending ? (
                  <div className="w-4">
                    <LoadingSpinner className="w-4 h-4" />
                  </div>
                ) : (
                  <div className="w-4">
                    <FontAwesomeIcon icon={faPaperPlane} className="w-4" />
                  </div>
                )}
                <span className="hidden sm:block">Send</span>
              </button>
              <button
                className="btn btn-md font-semibold hover:bg-warning hover:text-white hover:border-warning"
                onClick={handleEditNote}
              >
                <FontAwesomeIcon icon={faPenToSquare} />
                <span className="hidden sm:block">Edit</span>
              </button>
            </div>
          ) : null}

          <button
            className="btn btn-md font-semibold hover:bg-info hover:text-white hover:border-info"
            onClick={handleNewNote}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden sm:block">New</span>
          </button>

          <div className="dropdown dropdown-end">
            <div
              tabIndex={0}
              role="button"
              className="btn btn-ghost btn-circle avatar"
            >
              <div className="">
                <FontAwesomeIcon
                  icon={faCog}
                  className="text-main-gray text-xl"
                />
              </div>
            </div>
            <ul
              tabIndex={0}
              className="menu menu-sm dropdown-content bg-base-100 rounded-box  mt-3 w-52 p-2 shadow z-50 gap-1"
            >
              <li className="">
                <a
                  className="flex flex-row gap-2 w-full"
                  href={"mailto:bmacmahon@securedinvestmentcorp.com"}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FontAwesomeIcon icon={faHeadset} className="w-4" />
                  <span className="font-semibold">Support</span>
                </a>
              </li>
              <li className="">
                <button
                  onClick={handleLogout}
                  className="flex flex-row gap-2 w-full"
                >
                  <FontAwesomeIcon icon={faRightFromBracket} className="w-4" />
                  <span className="font-semibold">Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </PrivateComponent>
  );
};

export default AdminToolbar;
